"use client";

import { KeyboardEvent, useState } from "react";

import { Badge } from "../../shadcn/ui/badge";
import { Input } from "../../shadcn/ui/input";
import { X } from "lucide-react";
import { isValidEmail } from "@punchup/utils";

interface EmailChipsInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function EmailChipsInput({
  value,
  onChange,
  placeholder = "Add email and press Enter",
  disabled = false,
}: EmailChipsInputProps) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const tryAdd = (raw: string) => {
    const trimmed = raw.trim().toLowerCase();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      setError(`"${trimmed}" is not a valid email`);
      return;
    }
    if (value.includes(trimmed)) {
      setError(`${trimmed} is already in the list`);
      return;
    }
    onChange([...value, trimmed]);
    setDraft("");
    setError(null);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      tryAdd(draft);
    } else if (event.key === "Backspace" && !draft && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-neutral-300 bg-white p-1.5">
        {value.map((email) => (
          <Badge
            key={email}
            variant="secondary"
            className="flex items-center gap-1"
          >
            {email}
            {!disabled && (
              <button
                type="button"
                aria-label={`Remove ${email}`}
                onClick={() => onChange(value.filter((e) => e !== email))}
                className="rounded hover:bg-neutral-200"
              >
                <X size={12} />
              </button>
            )}
          </Badge>
        ))}
        <Input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            if (draft) tryAdd(draft);
          }}
          placeholder={value.length === 0 ? placeholder : ""}
          disabled={disabled}
          className="h-7 min-w-[200px] flex-1 border-0 bg-transparent p-0 px-1 text-sm shadow-none focus-visible:ring-0"
        />
      </div>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}
